import { Box, Typography, List, ListItem, ListItemButton, ListItemText, TextField, Tooltip } from "@mui/material";
import { Popover } from '@base-ui-components/react/popover';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import React, { useEffect, useRef } from "react";
import DefaultButton from "../general/buttons/DefaultButton";
import { apiFetch } from "../../services/api";
import { checkPlaylistLinked } from "../../services/playlist";
import * as appleAuth from "../../services/apple/apple-auth";
import { connectApple, connectSpotify } from "../../services/providerConnect";
import { MusicProvider } from "../../services/musicProvider";
import { colors } from "../../theme";
import { popupStyle, positionerStyle, closeButtonStyle, backdropStyle } from "../general/buttons/triggerStyles";

export type { MusicProvider };

async function linkPlaylist(playlistId: number, provider: MusicProvider, setIsLinked: (v: boolean) => void, selectedPlaylist: ExternalPlaylist) {
    return apiFetch(`/v1/playlist/link?playlistId=${playlistId}`, {
        method: 'POST',
        body: JSON.stringify({
            source: provider,
            sourceId: selectedPlaylist.id,
            sourcePlaylistName: selectedPlaylist.name
        })
    }).then(() => {
        setIsLinked(true);
    });
}

async function unlinkPlaylist(playlistId: number, provider: MusicProvider, setIsLinked: (v: boolean) => void) {
    return apiFetch(`/v1/playlist/link?playlistId=${playlistId}`, {
        method: 'POST',
        body: JSON.stringify({ source: provider, sourceId: 'unlink' })
    }).then(() => {
        setIsLinked(false);
    });
}

function getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()!.split(';').shift()!;
    }
    return null;
}

function deleteCookie(name: string): void {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/** Common shape both providers' "pick a playlist to link" list renders against. */
interface ExternalPlaylist {
    id: string;
    name: string;
    description: string;
}

/** Distinguishes "you need to connect this provider first" from any other load failure,
 *  so the popup can offer a Connect button instead of just an error string. */
class ProviderNotConnectedError extends Error {}

async function getSpotifyPlaylists(): Promise<ExternalPlaylist[]> {
    const spotifyToken = getCookie("spotifyAuthToken");
    if (!spotifyToken) {
        throw new ProviderNotConnectedError("No Spotify access token found.");
    }

    const response = await fetch("https://api.spotify.com/v1/me/playlists?limit=50", {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${spotifyToken}`,
            'Content-Type': 'application/json',
        }
    });

    if (!response.ok) {
        if (response.status === 401) {
            deleteCookie("spotifyAuthToken");
            throw new ProviderNotConnectedError("Spotify session expired.");
        }
        throw new Error(`Couldn't load your Spotify playlists (status ${response.status}).`);
    }

    const data = await response.json();
    const items = data.items || [];
    return items.map((p: any) => ({ id: p.id, name: p.name, description: p.description || "" }));
}

async function getApplePlaylists(): Promise<ExternalPlaylist[]> {
    if (!(await appleAuth.isLoggedIn())) {
        throw new ProviderNotConnectedError("Apple Music not connected.");
    }

    const music = appleAuth.getMusicKitInstance();
    const playlists = await music.api.library.playlists();
    return (playlists || []).map((p: any) => ({
        id: p.id,
        name: p.attributes?.name ?? "Untitled playlist",
        description: p.attributes?.description?.standard || "",
    }));
}

async function getUsersPlaylists(provider: MusicProvider): Promise<ExternalPlaylist[]> {
    return provider === "Spotify" ? getSpotifyPlaylists() : getApplePlaylists();
}

interface LinkButtonProps {
    playlistId: any;
    provider: MusicProvider;
    /** Lets the parent (the provider tab strip) show linked status without duplicating this fetch. */
    onLinkedChange?: (linked: boolean) => void;
}

export default function LinkButton(props: LinkButtonProps) {
    const { playlistId, provider, onLinkedChange } = props;
    const [isLinked, setIsLinkedState] = React.useState<boolean>(false);
    const [checkingLink, setCheckingLink] = React.useState<boolean>(true);
    const [userPlaylists, setUserPlaylists] = React.useState<ExternalPlaylist[]>([]);
    const [selectedPlaylist, setSelectedPlaylist] = React.useState<ExternalPlaylist | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [needsConnect, setNeedsConnect] = React.useState<boolean>(false);
    const [connecting, setConnecting] = React.useState<boolean>(false);
    const [searchTerm, setSearchTerm] = React.useState<string>("");

    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const setIsLinked = (linked: boolean) => {
        setIsLinkedState(linked);
        onLinkedChange?.(linked);
    };

    useEffect(() => {
        let cancelled = false;
        setCheckingLink(true);
        checkPlaylistLinked(playlistId, provider)
            .then(linked => { if (!cancelled) setIsLinked(linked); })
            .finally(() => { if (!cancelled) setCheckingLink(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playlistId, provider]);

    const filteredPlaylists = userPlaylists.filter(playlist =>
        playlist.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleLoadPlaylists = async () => {
        setLoading(true);
        setError(null);
        setNeedsConnect(false);
        try {
            setUserPlaylists(await getUsersPlaylists(provider));
        } catch (err: any) {
            if (err instanceof ProviderNotConnectedError) {
                setNeedsConnect(true);
            } else {
                setError(err?.message || 'Failed to load playlists');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = async () => {
        setConnecting(true);
        setError(null);
        try {
            if (provider === "Spotify") {
                // Full-page redirect - nothing after this runs in the current page.
                connectSpotify(false);
                return;
            }
            await connectApple();
            await handleLoadPlaylists();
        } catch (err: any) {
            setError(err?.message || `Couldn't connect ${provider}`);
        } finally {
            setConnecting(false);
        }
    };

    return (
        <Popover.Root>
            <Tooltip title={checkingLink ? "Checking link status…" : isLinked ? `Linked to ${provider}` : `Link to a ${provider} playlist`}>
                <Popover.Trigger
                    style={{
                        background: "none",
                        border: "none",
                        padding: 8,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 999,
                        color: isLinked ? colors.accent : "rgba(255,255,255,0.75)",
                        opacity: checkingLink ? 0.5 : 1,
                    }}
                    onClick={() => { if (!isLinked) handleLoadPlaylists(); }}
                >
                    {isLinked ? <LinkIcon sx={{ fontSize: 32 }} /> : <LinkOffIcon sx={{ fontSize: 32 }} />}
                </Popover.Trigger>
            </Tooltip>

            <Popover.Portal>
                <Popover.Backdrop style={backdropStyle} />
                <Popover.Positioner style={positionerStyle}>
                    <Popover.Popup style={{ ...popupStyle, width: 400 }}>
                        <Popover.Close ref={closeButtonRef} aria-label="Close" style={closeButtonStyle}>
                            ✕
                        </Popover.Close>

                        <Typography variant="h5" sx={{ mb: 0.5 }}>
                            {isLinked ? `Linked to ${provider}` : `Link to a ${provider} playlist`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2.5 }}>
                            {isLinked
                                ? `Syncing pushes this Shufl playlist into your linked ${provider} playlist.`
                                : `Choose which of your ${provider} playlists this should sync into.`}
                        </Typography>

                        {isLinked ? (
                            <DefaultButton
                                text="Unlink playlist"
                                variant="secondary"
                                fullWidth
                                onClick={async () => {
                                    try {
                                        await unlinkPlaylist(playlistId, provider, setIsLinked);
                                        closeButtonRef.current?.click();
                                    } catch (err: any) {
                                        setError(err?.message || 'Failed to unlink');
                                    }
                                }}
                            />
                        ) : needsConnect ? (
                            <>
                                <Typography sx={{ fontSize: 13, color: colors.textMuted, mb: 2 }}>
                                    Connect your {provider} account to pick a playlist to sync into.
                                </Typography>
                                {error && (
                                    <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                                        {error}
                                    </Typography>
                                )}
                                <DefaultButton
                                    text={connecting ? "Connecting…" : `Connect ${provider}`}
                                    fullWidth
                                    disabled={connecting}
                                    onClick={handleConnect}
                                />
                            </>
                        ) : (
                            <>
                                {userPlaylists.length > 0 && (
                                    <TextField
                                        placeholder="Search your playlists"
                                        fullWidth
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        sx={{ mb: 1.5 }}
                                    />
                                )}

                                {loading && <Typography variant="body2">Loading your playlists…</Typography>}

                                {error && (
                                    <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                                        {error}
                                    </Typography>
                                )}

                                {!loading && !error && userPlaylists.length === 0 && (
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        No playlists loaded yet.
                                    </Typography>
                                )}

                                {userPlaylists.length > 0 && (
                                    <Box sx={{ maxHeight: 260, overflowY: "auto", mb: 2 }}>
                                        <List disablePadding>
                                            {filteredPlaylists.map((playlist) => (
                                                <ListItem key={playlist.id} disablePadding sx={{ mb: 0.5 }}>
                                                    <ListItemButton
                                                        selected={selectedPlaylist?.id === playlist.id}
                                                        onClick={() => setSelectedPlaylist(playlist)}
                                                        sx={{
                                                            borderRadius: 1,
                                                            "&.Mui-selected": {
                                                                backgroundColor: colors.accentSoft,
                                                                "&:hover": { backgroundColor: colors.accentSoft },
                                                            },
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={playlist.name}
                                                            secondary={playlist.description || "No description"}
                                                            slotProps={{
                                                                primary: { fontSize: 14, fontWeight: 600 },
                                                                secondary: { fontSize: 12, color: colors.textMuted },
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                <DefaultButton
                                    text="Link playlist"
                                    fullWidth
                                    disabled={!selectedPlaylist}
                                    onClick={async () => {
                                        if (!selectedPlaylist) return;
                                        try {
                                            await linkPlaylist(playlistId, provider, setIsLinked, selectedPlaylist);
                                            closeButtonRef.current?.click();
                                        } catch (err: any) {
                                            setError(err?.message || 'Failed to link playlist');
                                        }
                                    }}
                                />
                            </>
                        )}
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}
